import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Filter, ChevronRight } from "lucide-react";
import PageLayout from "../components/layout/PageLayout";
import api from "../lib/api";

const SPORTS = ["All", "Football", "Basketball"];
const STATES = ["All", "NE", "IA", "KS", "MO", "SD"];
const GRAD = ["All", "2026", "2027", "2028", "2029"];
const SKELETON_KEYS = ["sk-a", "sk-b", "sk-c", "sk-d", "sk-e", "sk-f"];

export default function Athletes() {
    const [athletes, setAthletes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [sport, setSport] = useState("All");
    const [state, setState] = useState("All");
    const [grad, setGrad] = useState("All");

    const fetchAthletes = useCallback(() => {
        setLoading(true);
        const params = {};
        if (sport !== "All") params.sport = sport;
        if (state !== "All") params.state = state;
        if (grad !== "All") params.grad_year = grad;
        if (q) params.q = q;
        api.get("/athletes", { params }).then(r => setAthletes(r.data)).finally(() => setLoading(false));
    }, [sport, state, grad, q]);

    useEffect(() => { fetchAthletes(); }, [fetchAthletes]);

    return (
        <PageLayout>
            <section className="pt-32 pb-10 border-b border-white/10" data-testid="athletes-directory">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10">
                    <div className="section-label mb-5">Digital Beast Roster</div>
                    <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
                        <h1 className="font-display text-5xl md:text-7xl text-white leading-none tracking-tight" data-testid="athletes-heading">
                            THE <span className="text-[#d4af37]">ATHLETES</span>
                        </h1>
                        <div className="text-white/50 text-sm max-w-md">
                            Explore the athletes represented by Digital Beast. Filter by sport, state, position and graduation year.
                        </div>
                    </div>

                    <div className="mt-10 grid md:grid-cols-4 gap-3">
                        <div className="md:col-span-2 flex items-center gap-3 surface px-4">
                            <Search size={18} className="text-white/50" />
                            <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && fetchAthletes()}
                                placeholder="Search athletes, schools..."
                                className="w-full bg-transparent border-none outline-none py-4 text-white placeholder:text-white/40 text-sm"
                                data-testid="athletes-search-input" />
                        </div>
                        <button onClick={fetchAthletes} className="btn-outline justify-center" data-testid="athletes-search-btn">
                            <Filter size={14} /> Apply
                        </button>
                        <div className="flex items-center gap-2 text-white/50 text-sm justify-end">
                            <span className="font-display text-3xl text-[#d4af37]" data-testid="athletes-count">{athletes.length}</span>
                            <span className="uppercase tracking-widest text-xs">athletes</span>
                        </div>
                    </div>

                    <div className="mt-6 flex flex-wrap gap-2" data-testid="athletes-filters">
                        <div className="text-xs text-white/40 uppercase tracking-widest mr-2 flex items-center">Sport</div>
                        {SPORTS.map(s => (
                            <button key={s} onClick={() => setSport(s)} data-testid={`filter-sport-${s.toLowerCase()}`}
                                className={`chip ${sport === s ? "chip-active" : ""}`}>{s}</button>
                        ))}
                        <div className="text-xs text-white/40 uppercase tracking-widest mr-2 ml-4 flex items-center">State</div>
                        {STATES.map(s => (
                            <button key={s} onClick={() => setState(s)} data-testid={`filter-state-${s.toLowerCase()}`}
                                className={`chip ${state === s ? "chip-active" : ""}`}>{s}</button>
                        ))}
                        <div className="text-xs text-white/40 uppercase tracking-widest mr-2 ml-4 flex items-center">Grad</div>
                        {GRAD.map(s => (
                            <button key={s} onClick={() => setGrad(s)} data-testid={`filter-grad-${s.toLowerCase()}`}
                                className={`chip ${grad === s ? "chip-active" : ""}`}>{s}</button>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-16">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10">
                    {loading ? (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {SKELETON_KEYS.map(k => (
                                <div key={k} className="aspect-[3/4] surface animate-pulse" />
                            ))}
                        </div>
                    ) : athletes.length === 0 ? (
                        <div className="py-24 text-center">
                            <div className="text-white/40 text-lg">No athletes match your filters.</div>
                            <button onClick={() => { setSport("All"); setState("All"); setGrad("All"); setQ(""); }} className="btn-outline mt-6" data-testid="clear-filters">Clear filters</button>
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="athletes-grid">
                            {athletes.map(a => (
                                <Link key={a.id} to={`/athletes/${a.id}`}
                                    className="group relative block aspect-[3/4] overflow-hidden surface surface-hover"
                                    data-testid={`athlete-card-${a.id}`}>
                                    <img src={a.hero_image} alt={a.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[900ms]" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                                    <div className="absolute top-4 left-4 right-4 flex justify-between items-start text-[10px] tracking-[0.24em] uppercase text-white/70">
                                        <span>{a.sport} · {a.position}</span>
                                        {a.featured && <span className="text-[#d4af37]">★ Featured</span>}
                                    </div>
                                    <div className="absolute bottom-0 left-0 right-0 p-5">
                                        <div className="font-display text-3xl text-white leading-none">{a.name.toUpperCase()}</div>
                                        <div className="text-white/70 text-xs uppercase tracking-widest mt-2">{a.school}</div>
                                        <div className="mt-3 flex justify-between items-end">
                                            <div className="text-[#d4af37] text-xs font-mono">{(a.social?.followers || 0).toLocaleString()} followers</div>
                                            <div className="flex items-center gap-1 text-white/80 text-xs group-hover:text-[#d4af37] transition-colors">
                                                View <ChevronRight size={12} />
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </PageLayout>
    );
}
